import { Notifications as NotificationsIcon } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import notificacaoService from "../services/notificacaoService";

const NotificationMenu = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificacoes, setNotificacoes] = useState([]);
  const [tarefasPendentes, setTarefasPendentes] = useState([]);
  const [contador, setContador] = useState(0);
  const [contadorDetalhes, setContadorDetalhes] = useState({
    total: 0,
    notificacoes: 0,
    tarefas: 0,
  });
  const [loading, setLoading] = useState(false);

  // Buscar contador de notificações a cada 5 minutos
  useEffect(() => {
    buscarContador();
    const interval = setInterval(buscarContador, 300000); // 5 minutos

    return () => clearInterval(interval);
  }, []);

  const buscarContador = async () => {
    try {
      const dados = await notificacaoService.contarNaoLidas();
      setContador(dados?.total || 0);
      setContadorDetalhes({
        total: dados?.total || 0,
        notificacoes: dados?.notificacoes || 0,
        tarefas: dados?.tarefas || 0,
      });
    } catch (error) {
      console.error("Erro ao buscar contador de notificações:", error);
      setContador(0);
      setContadorDetalhes({
        total: 0,
        notificacoes: 0,
        tarefas: 0,
      });
    }
  };

  const buscarNotificacoes = async () => {
    try {
      setLoading(true);
      const data = await notificacaoService.listarNaoLidas();
      const notificacoesComuns = (data || []).filter(
        (item) => item.origem !== "tarefa",
      );
      const tarefas = (data || []).filter((item) => item.origem === "tarefa");
      setNotificacoes(notificacoesComuns);
      setTarefasPendentes(tarefas);
    } catch (error) {
      console.error("Erro ao buscar notificações:", error);
      setNotificacoes([]);
      setTarefasPendentes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    buscarNotificacoes();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificacaoClick = async (notificacao) => {
    try {
      if (notificacao.origem === "tarefa") {
        navigate(`/tarefas?edit=${notificacao.entidade_id}`);
        handleClose();
        return;
      }

      // Marcar como lida
      await notificacaoService.marcarComoLida(notificacao.id);

      // Atualizar contador
      setContador((prev) => Math.max(0, prev - 1));
      setContadorDetalhes((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        notificacoes: Math.max(0, prev.notificacoes - 1),
      }));

      setNotificacoes((prevNotificacoes) =>
        prevNotificacoes.filter((item) => item.id !== notificacao.id),
      );

      // Navegar para a página correta com query param para abrir o modal
      if (notificacao.tipo === "mencao_tarefa") {
        navigate(`/tarefas?edit=${notificacao.entidade_id}`);
      } else if (notificacao.tipo === "mencao_obrigacao") {
        navigate(`/obrigacoes?edit=${notificacao.entidade_id}`);
      }

      handleClose();
    } catch (error) {
      console.error("Erro ao processar clique na notificação:", error);
    }
  };

  const handleMarcarTodasLidas = async () => {
    try {
      await notificacaoService.marcarTodasComoLidas();
      setContador(0);
      setNotificacoes([]);
      setContadorDetalhes((prev) => ({
        total: prev.tarefas,
        notificacoes: 0,
        tarefas: prev.tarefas,
      }));
      await buscarContador();
      handleClose();
    } catch (error) {
      console.error("Erro ao marcar todas como lidas:", error);
    }
  };

  const construirDataBrasil = (dataStr, horaStr) => {
    if (!dataStr) return null;
    try {
      const [ano, mes, dia] = dataStr.split("-").map((v) => parseInt(v, 10));
      const [hora = 12, minuto = 0] = (horaStr || "12:00")
        .split(":")
        .map((v) => parseInt(v, 10));

      if ([ano, mes, dia].some((v) => Number.isNaN(v))) {
        return null;
      }

      return new Date(ano, mes - 1, dia, hora, minuto, 0, 0);
    } catch (error) {
      console.error("Erro ao construir data:", error);
      return null;
    }
  };

  const formatarDataRelativa = (dataStr, horaStr) => {
    if (!dataStr) return "";
    const data =
      typeof dataStr === "string" && dataStr.length === 10
        ? construirDataBrasil(dataStr, horaStr)
        : new Date(dataStr);

    if (!data || Number.isNaN(data.getTime())) {
      return "";
    }

    const agora = new Date();
    const diffMs = data.getTime() - agora.getTime();
    const futuro = diffMs > 0;
    const diffMins = Math.floor(Math.abs(diffMs) / 60000);

    if (diffMins < 1) return "Agora";
    if (diffMins < 60) {
      return futuro ? `Em ${diffMins} min` : `Há ${diffMins} min`;
    }
    const diffHoras = Math.floor(diffMins / 60);
    if (diffHoras < 24) {
      return futuro ? `Em ${diffHoras} h` : `Há ${diffHoras} h`;
    }
    const diffDias = Math.floor(diffHoras / 24);
    return futuro
      ? `Em ${diffDias} dia${diffDias > 1 ? "s" : ""}`
      : `Há ${diffDias} dia${diffDias > 1 ? "s" : ""}`;
  };

  const resumoNotificacoes =
    notificacoes.length > 0
      ? notificacoes.length
      : contadorDetalhes.notificacoes || 0;
  const resumoTarefas =
    tarefasPendentes.length > 0
      ? tarefasPendentes.length
      : contadorDetalhes.tarefas || 0;

  return (
    <>
      <IconButton onClick={handleClick} sx={{ color: "#333" }}>
        <Badge badgeContent={contador} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { width: 380, maxHeight: 480 },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6">Notificações</Typography>
            <Typography variant="body2" color="text.secondary">
              Resumo: {resumoNotificacoes} notificações • {resumoTarefas}{" "}
              tarefas
            </Typography>
          </Box>
          {notificacoes.length > 0 && (
            <Button
              size="small"
              onClick={handleMarcarTodasLidas}
              sx={{ textTransform: "none" }}
            >
              Marcar todas como lidas
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">Carregando...</Typography>
          </Box>
        ) : notificacoes.length === 0 && tarefasPendentes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              Nenhuma notificação nova
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 360, overflow: "auto" }}>
            {tarefasPendentes.length > 0 && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  Tarefas pendentes e em andamento
                </Typography>
                {tarefasPendentes.map((notificacao) => (
                  <MenuItem
                    key={notificacao.id}
                    onClick={() => handleNotificacaoClick(notificacao)}
                    sx={{
                      whiteSpace: "normal",
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f0f0f0",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          {notificacao.detalhes?.cliente || "Minhas tarefas"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatarDataRelativa(
                            notificacao.data_vencimento ||
                              notificacao.data_criacao,
                            notificacao.hora_vencimento,
                          )}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {notificacao.mensagem}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Box>
            )}
            {tarefasPendentes.length > 0 && notificacoes.length > 0 && (
              <Divider />
            )}
            {notificacoes.map((notificacao) => (
              <MenuItem
                key={notificacao.id}
                onClick={() => handleNotificacaoClick(notificacao)}
                sx={{
                  whiteSpace: "normal",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid #f0f0f0",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {notificacao.remetente?.nome || "Sistema"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatarDataRelativa(notificacao.data_criacao)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {notificacao.mensagem}
                  </Typography>
                  {notificacao.origem === "tarefa" &&
                    notificacao.detalhes?.cliente && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Cliente: {notificacao.detalhes.cliente}
                      </Typography>
                    )}
                </Box>
              </MenuItem>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;
